<template>
  <div v-show="isActive" class="map-tool-window layer-tool-window" :id="isFixed ? 'layersIDFix' : 'layersID'">
    <div v-if="!isFixed" class="map-tool-window-header" :class="{ widgetHeader: !isFixed }" id="layersIDheader">
      <p class="map-tool-window-header-title">Lista de capas</p>
      <div class="map-tool-window-header-actions">
        <button v-if="canManageLayers" class="control-btn" v-on:click="onClickModalNewLayer()">
          Añadir capa
        </button>

        <button class="control-btn" v-on:click="isHidden = !isHidden">
          <!-- <font-awesome-icon v-if="!isHidden" icon="minus"></font-awesome-icon>
          <font-awesome-icon v-else icon="plus"></font-awesome-icon> -->
         <svg  v-if="isHidden"
         viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
         <svg v-else
          viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
        <button class="control-btn" v-on:click="exitLayers()">
          <!-- <font-awesome-icon icon="times"></font-awesome-icon> -->
                 <svg viewBox="0 0 24 24"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div v-show="!isHidden" class="search-filter-section">
      <!-- Search Bar -->
      <div class="search-bar-container">
        <div class="search-bar">
          <font-awesome-icon icon="search" class="search-icon" />

          <!-- Typing dots indicator -->
          <transition name="fade">
            <div v-if="isDebouncing" class="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </transition>

          <input ref="searchInput" type="text" v-model="searchQuery" placeholder="Buscar capas..." class="search-input"
            aria-label="Buscar capas por nombre" @keydown.esc="clearSearch" @keydown.enter="forceSearch" />
          <transition name="fade">
            <button v-if="searchQuery" @click="clearSearch" class="clear-button" aria-label="Limpiar búsqueda"
              type="button">
              <font-awesome-icon icon="times" />
            </button>
          </transition>

          <!-- Pulse bar indicator -->
          <transition name="fade">
            <div v-if="isDebouncing" class="debounce-pulse-bar"></div>
          </transition>
        </div>
      </div>

      <!-- Filter Type Selector -->
      <div class="filter-type-selector" role="group" aria-label="Tipo de filtro">
        <button v-for="filter in filterTypes" :key="filter.value" @click="setFilterType(filter.value)"
          :class="['filter-btn', { active: currentFilterType === filter.value }]"
          :aria-pressed="currentFilterType === filter.value" type="button">
          {{ filter.label }}
        </button>
      </div>

      <!-- Result Summary -->
      <transition name="fade">
        <div v-if="hasSearchQuery || hasActiveFilters" class="result-summary">
          <span class="result-count">
            {{ filteredLayersCount }}
            {{ filteredLayersCount === 1 ? "capa encontrada" : "capas encontradas" }}
          </span>
          <button @click="resetAllFilters" class="reset-button" type="button">
            {{ resetButtonLabel }}
          </button>
        </div>
      </transition>

      <div v-if="filteredCategories.length" class="collapse-actions">
        <button @click="expandAllCategories" class="link-button" type="button">Abrir todo</button>
        <button @click="collapseAllCategories" class="link-button" type="button">Colapsar todo</button>
      </div>
    </div>

    <div class="map-tool-window-body" v-show="!isHidden">
      <div :class="{ resizable: !isFixed }">
        <!-- Loading State -->
        <div v-if="isSearching" class="loading-state">
          <div class="skeleton-item" v-for="n in 3" :key="n"></div>
        </div>

        <!-- Empty State -->
        <div v-else-if="
          filteredLayersCount === 0 && (hasSearchQuery || hasActiveFilters)
        " class="empty-state">
          <font-awesome-icon icon="search" class="empty-icon" />
          <h3>No se encontraron capas</h3>
          <p>Intenta ajustar tu búsqueda o filtros</p>
          <button @click="resetAllFilters" class="btn-clean-filter" type="button">
            Limpiar filtros
          </button>
        </div>

        <!-- Results List -->
        <ListPlegable v-else v-for="(category, i) in filteredCategories" ref="listPlegable" :key="i" :category="category" :index="i"
          :check-state="categoryCheckState(category)" :search-query="hasSearchQuery ? debouncedSearchQuery : ''"
          :is-search-active="hasSearchQuery" @selectLayer="onClickSelectLayer" @removeLayer="onClickRemoveLayer"
          @layerAttributes="getLayerInfo" @updateOpacity="onUpdateOpacity" @toggleCategory="onToggleCategory"
          @zoomLayer="onClickZoomLayer" @toggleLayerFilter="onToggleLayerFilter"
          @selectSymbology="onSelectSymbology" />

        <!-- Capas externas WMS (agregadas por URL durante la sesión, no persistidas) -->
        <p v-if="wmsExternalCategories.length" class="wms-ext-heading">Capas externas (WMS)</p>
        <ListPlegable v-for="(category, i) in wmsExternalCategories" :key="category.id" :category="category"
          :index="filteredCategories.length + i" :is-external="true"
          :check-state="wmsCheckState(category)"
          @toggleVisibility="toggleWmsVisibility" @updateOpacity="onWmsUpdateOpacity"
          @deleteExternalLayer="removeWmsLayer" @toggleCategoryVisibility="toggleWmsCategory"
          @zoomLayer="onClickZoomWmsLayer" />

        <!-- Sección Terreno: capas hermanas, no una categoría más del catálogo (ver TerrainSection.vue) -->
        <TerrainSection
          :filter-type="currentFilterType"
          :search-query="hasSearchQuery ? debouncedSearchQuery : ''"
          :is-search-active="hasSearchQuery"
          @filtered-count="onTerrainFilteredCount"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ListPlegable from "@/components/mapTools/layers/ListPlegable.vue";
import TerrainSection from "@/components/mapTools/layers/TerrainSection.vue";
import { draggableDiv } from "@/components/mixin/draggableDiv.js";
import { vuex } from "@/components/mixin/vuex.js";
import { searchable } from "@/components/mixin/searchable.js";
import { filterable } from "@/components/mixin/filterable.js";
import { restLayerActivity } from "@/components/mixin/restLayerActivity.js";
import { LayerService } from "@/services/layers/layerService.js";
import { mapService } from "@/services/map/mapService";
import { attributesTableService } from "@/services/layers/attributesTable";
import {
  IMPORT_CATEGORY_ID,
  IMPORT_CATEGORY_NAME,
  IMPORT_PROVIDER,
  createImportedDescriptor,
  fileDatasets,
  importedLayerRegistry,
} from "@/services/layers/importedLayerRegistry";
import { GEOPROCESS_LAYER_ZINDEX } from "@/components/mapTools/geoprocessings/utils/geoprocessZIndex.js";

export default {
  name: "HelloWorld",
  props: ["isFixed"],
  mixins: [draggableDiv, vuex, searchable, filterable, restLayerActivity],
  data() {
    return {
      isHidden: false,
      filterTypes: [
        { value: "all", label: "Todas" },
        { value: "active", label: "Solo Activas" },
      ],
      terrainFilteredLayersCount: 0,
    };
  },
  components: {
    ListPlegable,
    TerrainSection,
  },
  computed: {
    // El botón "Añadir capa" abre /capas/nueva-capa, que es la sección que
    // habilita el permiso layers. Sin el permiso la vista rebota, así que el
    // botón no tiene por qué ofrecerse (QA-172).
    canManageLayers: function () {
      var permissions = this.$store.getters.getPermissions;
      return !!(permissions && permissions.layers);
    },
    isActive: function () {
      return this.isFixed
        ? this.isMobile
        : this.mapToolValues("layers").active && !this.isMobile;
    },

    /**
     * Get layers from Vuex store
     */
    layers() {
      const categories = this.$store.getters.getLayers || [];
      const imported = this.$store.getters.getImportedLayers || [];
      if (!imported.length) return categories;
      return [
        ...categories,
        { id: IMPORT_CATEGORY_ID, name: IMPORT_CATEGORY_NAME, layers: imported },
      ];
    },

    /**
     * Get filtered categories based on search and filters
     * IMPORTANT: Apply filters in the correct order:
     * 1. Active filter (filter which layers within categories)
     * 2. Search (search within the filtered set)
     */
    filteredCategories() {
      let categories = this.layers;

      // Step 1: Apply active filter (filter layers within categories)
      if (this.currentFilterType === "active") {
        categories = categories
          .map((cat) => ({
            ...cat,
            layers: cat.layers ? cat.layers.filter((layer) => layer.selected) : [],
          }))
          .filter((cat) => cat.layers.length > 0);
      }

      // Step 2: Apply search LAST (search within the filtered categories)
      if (this.hasSearchQuery) {
        categories = this.searchCategories(categories);
      }

      return categories;
    },

    /**
     * Count total filtered layers
     */
    filteredLayersCount() {
      const catalogCount = this.filteredCategories.reduce(
        (count, cat) => count + (cat.layers ? cat.layers.length : 0),
        0
      );
      return catalogCount + this.terrainFilteredLayersCount;
    },

    /**
     * Get the appropriate label for the reset/clear button
     */
    resetButtonLabel() {
      if (this.currentFilterType === "active") {
        return "Apagar Todos";
      }
      return "Limpiar todo";
    },

    /**
     * Capas WMS agregadas por URL durante la sesión (no persistidas)
     */
    wmsSessionLayers() {
      return this.$store.getters.getWmsSessionLayers;
    },

    /**
     * Capas WMS de sesión agrupadas por servidor (un WMS puede tener varias capas),
     * con la misma forma {id, name, layers} que usa ListPlegable para las capas persistidas.
     */
    wmsExternalCategories() {
      const groups = {};
      this.wmsSessionLayers.forEach((layer) => {
        const url = layer.provider.url;
        if (!groups[url]) {
          groups[url] = { id: url, name: layer.provider.sourceLabel || url, layers: [] };
        }
        groups[url].layers.push(layer);
      });
      return Object.values(groups);
    },
  },

  methods: {
    async syncImportedFiles() {
      const files = await LayerService.getUploadedFilesByMap(this.$route.params.idMap);
      // Un archivo puede traer varios datasets: hay que expandirlos igual que
      // UploadShpKmz.getFilesByMap, que despacha setImportedLayers para los mismos
      // archivos. Si los ids no coinciden, el último panel abierto pisa al otro con
      // descriptores que no calzan con lo registrado en importedLayerRegistry.
      const descriptors = (Array.isArray(files) ? files : []).reduce((rows, file) =>
        rows.concat(fileDatasets(file).map(dataset => {
          const descriptor = createImportedDescriptor(file, false, dataset);
          descriptor.selected = LayerService.layerExists(descriptor.id);
          return descriptor;
        })), []);
      this.$store.dispatch("setImportedLayers", descriptors);
    },
    /**
     * Perform search on button click
     * Overrides searchable mixin method
     */
    performSearch() {
      if (!this.searchQuery.trim()) return;

      this.isSearching = true;
      this.addToSearchHistory();

      // Use setTimeout to show loading state briefly
      setTimeout(() => {
        this.isSearching = false;
      }, 100);
    },

    /**
     * Search through categories and layers
     * @param {Array} categories - Categories to search
     * @returns {Array} Filtered categories with search metadata
     */
    searchCategories(categories) {
      const query = this.searchQuery.toLowerCase().trim();
      if (!query) return categories;

      return categories
        .map((category) => {
          const categoryMatches = category.name.toLowerCase().includes(query);
          const matchingLayers = category.layers
            ? category.layers.filter((layer) => layer.name.toLowerCase().includes(query))
            : [];

          if (categoryMatches || matchingLayers.length > 0) {
            return {
              ...category,
              layers: categoryMatches ? category.layers : matchingLayers,
              isMatch: categoryMatches,
              hasMatchingChildren: matchingLayers.length > 0,
              autoExpand: true, // Signal to child component to auto-expand
            };
          }
          return null;
        })
        .filter(Boolean);
    },

    /**
     * Set filter type
     */
    setFilterType(type) {
      this.currentFilterType = type;
      this.saveFilterState();
    },

    onTerrainFilteredCount(count) {
      this.terrainFilteredLayersCount = count;
    },

    /**
     * Reset filters based on current filter type
     * - In "Solo Activas": Uncheck all active layers, return to "Todos"
     * - Otherwise: Clear everything and return to "Todos"
     */
    resetAllFilters() {
      if (this.currentFilterType === "active") {
        // Apagar Todos: Uncheck all active layers, then return to "Todos"
        this.uncheckAllActiveLayers();
        this.clearSearch();
        this.currentFilterType = "all";
      } else {
        // Default: Clear everything and return to "Todos"
        this.clearSearch();
        this.clearAllFilters();
      }
    },

    /**
     * Expand/collapse every category section at once
     */
    expandAllCategories() {
      (this.$refs.listPlegable || []).forEach((list) => list.expand());
    },
    collapseAllCategories() {
      (this.$refs.listPlegable || []).forEach((list) => list.collapse());
    },

    /**
     * Uncheck all active layers (set selected = false)
     */
    uncheckAllActiveLayers() {
      // Snapshot: getActiveLayers returns the live store array, and
      // removeActiveLayer splices it — iterating the live array would skip
      // alternate elements.
      const activeLayers = [...(this.$store.getters.getActiveLayers || [])];

      activeLayers.forEach((activeLayer) => {
        const originalLayer = this.findLayerById(activeLayer.id);
        if (originalLayer) {
          this.$set(originalLayer, "selected", false);
          if (originalLayer.provider?.name === IMPORT_PROVIDER) {
            LayerService.deactivateImportedLayer(originalLayer.id);
            importedLayerRegistry.restore(originalLayer.id);
          } else {
            LayerService.removeLayerByID(originalLayer.id);
          }
          this.$store.dispatch("removeActiveLayer", originalLayer);
          this.endLayerSession(originalLayer);
        }
      });
    },

    /**
     * Handle layer selection
     * @param {Object} category - Category object
     * @param {Object} layer - Layer object to select
     */
  onClickSelectLayer: async function (category, layer) {
    console.log("Seleccionando capa a cargar:", layer);
    console.log("Categoría de la capa:", category);

    const originalLayer = this.findLayerById(layer.id);

    if (!originalLayer) {
      console.error("Layer not found in store:", layer);
      return;
    }

    // Agrega el nombre de la categoría a la capa original.
    // Se utiliza $set porque el proyecto está en Vue 2 y la propiedad
    // category_name puede no existir originalmente.
    this.$set(
      originalLayer,
      "category_name",
      category?.name || "Sin categoría"
    );

    if (originalLayer.provider?.name === IMPORT_PROVIDER) {
      try {
        const entry = await LayerService.loadLayerFromBackend({
          id: originalLayer.fileId,
          name: originalLayer.fileName,
          display_name: originalLayer.name,
        }, originalLayer);
        if (!entry || !originalLayer.selected) return;
        const activeLayer = { ...originalLayer, selected: true };
        entry.descriptor = activeLayer;
        this.$store.dispatch("upsertImportedLayer", activeLayer);
        this.$store.dispatch("addActiveLayer", activeLayer);
        await attributesTableService.applySavedFilterToMap(activeLayer).catch(error => {
          this.$store.dispatch("toast/show", { message: error.message || "La capa se activó, pero no se pudo restaurar su filtro", type: "warning" });
        });
      } catch (error) {
        this.$store.dispatch("upsertImportedLayer", { ...originalLayer, selected: false });
        this.$store.dispatch("toast/show", { message: error.message || "No se pudo activar la capa importada", type: "error" });
      }
      return;
    }

    // Si un geoproceso activo ya la había agregado recortada al área de
    // influencia, hay que reemplazarla por la versión sin recorte: ahora el
    // usuario también la activó, así que se ve en todo el mapa.
    if (this.$store.getters.getGeoprocessLayerRequest(originalLayer.id) && mapService.layerExists(originalLayer.id)) {
      LayerService.removeLayerByID(originalLayer.id);
    }
    LayerService.addLayerToMap(originalLayer, null, false);
    this.$store.dispatch("addActiveLayer", originalLayer);
    this.startLayerSession(originalLayer, this.$route.params.idMap);
  },

    async onToggleLayerFilter(category, layer) {
      const originalLayer = this.findLayerById(layer.id);
      if (!originalLayer) return;
      const active = (this.$store.getters.getActiveLayers || []).some(item => item.id === originalLayer.id);
      if (!active) {
        this.$set(originalLayer, "selected", true);
        await this.onClickSelectLayer(category, originalLayer);
        return;
      }
      try {
        await attributesTableService.toggleSavedFilter(originalLayer);
      } catch (error) {
        this.$store.dispatch("toast/show", { message: error.message || "No se pudo cambiar el estado del filtro", type: "error" });
      }
    },

    /**
     * Compute category checkbox state from active layers
     * @param {Object} category - Category object (may be a filtered copy)
     * @returns {'all'|'some'|'none'}
     */
    categoryCheckState: function (category) {
      const original = this.layers.find((c) => c.id === category.id);
      const catLayers = (original && original.layers) || [];
      if (catLayers.length === 0) return "none";
      const activeIds = new Set(
        (this.$store.getters.getActiveLayers || []).map((l) => l.id)
      );
      const activeCount = catLayers.filter((l) => activeIds.has(l.id)).length;
      if (activeCount === 0) return "none";
      return activeCount === catLayers.length ? "all" : "some";
    },

    /**
     * Cambia la simbología elegida por el usuario para una capa (evento de ListPlegable).
     * @param {Object} layer - Capa del catálogo
     * @param {String} symbologyId - Id de la simbología, '' para "Predeterminada"
     */
    onSelectSymbology(layer, symbologyId) {
      this.$store.commit("symbology/select", { layerId: layer.id, symbologyId: symbologyId || null });
      // Si la capa está apagada, applyLayerSymbology sólo hace console.warn: la
      // selección queda en el store y se aplica al encenderla (mapService.addLayerToMap).
      mapService.applyLayerSymbology(layer.id, this.$store.getters["symbology/activeRenderer"](layer.id));
    },

    /**
     * Toggle all layers in a category
     * (operates on the ORIGINAL category from the store, not the filtered copy)
     * @param {Object} category - Category object (may be a filtered copy)
     */
    onToggleCategory: async function (category) {
      const original = this.layers.find((c) => c.id === category.id);
      if (!original || !original.layers || original.layers.length === 0) return;
      const activeIds = new Set(
        (this.$store.getters.getActiveLayers || []).map((l) => l.id)
      );
      const allActive = original.layers.every((l) => activeIds.has(l.id));
      if (original.id === IMPORT_CATEGORY_ID) {
        if (allActive) {
          original.layers.forEach(layer => this.onClickRemoveLayer(original, layer));
        } else {
          for (const layer of original.layers) {
            if (!activeIds.has(layer.id)) await this.onClickSelectLayer(original, layer);
          }
        }
        return;
      }
      if (allActive) {
        // checked → deactivate all
        original.layers.forEach((layer) => {
          this.$set(layer, "selected", false);
          LayerService.removeLayerByID(layer.id);
          this.$store.dispatch("removeActiveLayer", layer);
          this.endLayerSession(layer);
        });
      } else {
        // unchecked or indeterminate → activate all missing
        original.layers.forEach((layer) => {
          if (activeIds.has(layer.id)) return;
          this.$set(layer, "selected", true);
          LayerService.addLayerToMap(layer, null, false);
          this.$store.dispatch("addActiveLayer", layer);
          this.startLayerSession(layer, this.$route.params.idMap);
        });
      }
    },

    /**
     * Handle layer removal
     * @param {Object} category - Category object
     * @param {Object} layer - Layer object to remove
     */
    onClickRemoveLayer: async function (category, layer) {
      console.log("Se selecciono para eliminar la capa:", layer);
      if (layer.provider?.name === IMPORT_PROVIDER) {
        LayerService.deactivateImportedLayer(layer.id);
        importedLayerRegistry.restore(layer.id);
        this.$store.dispatch("upsertImportedLayer", { ...layer, selected: false });
        this.$store.dispatch("removeActiveLayer", layer);
        if (this.$store.getters["attributeTable/layer"]?.id === layer.id) {
          this.$store.dispatch("attributeTable/closeTable");
        }
        return;
      }

      // Si un geoproceso activo todavía necesita esta capa, no desaparece del
      // mapa: se reemplaza por la versión recortada al área de influencia.
      const geoprocessRequest = this.$store.getters.getGeoprocessLayerRequest(layer.id);
      LayerService.removeLayerByID(layer.id);

      // Remove from active layers in store
      const originalLayer = this.findLayerById(layer.id);
      if (originalLayer) {
        this.$store.dispatch('removeActiveLayer', originalLayer);
        this.endLayerSession(originalLayer);
      }

      if (geoprocessRequest) {
        await LayerService.addLayerToMap(geoprocessRequest.layer, geoprocessRequest.filterGeometry, false);
        this.$store.dispatch("addGeoprocessResultLayer", geoprocessRequest.layer);
        const map = mapService.get();
        if (map) {
          map.getLayers().forEach((mapLayer) => {
            if (mapLayer && String(mapLayer.get("id")) === String(layer.id)) {
              mapLayer.setZIndex(GEOPROCESS_LAYER_ZINDEX);
            }
          });
        }
      }
    },
    /**
     * Activate layer if needed, then zoom to it
     * @param {Object} category - Category object
     * @param {Object} layer - Layer object to zoom to
     */
    onClickZoomLayer: function (category, layer) {
      const originalLayer = this.findLayerById(layer.id);
      if (!originalLayer) {
        console.error("Layer not found in store:", layer);
        return;
      }
      const isActive = (this.$store.getters.getActiveLayers || []).some(
        (l) => l.id === originalLayer.id
      );
      if (originalLayer.provider?.name === IMPORT_PROVIDER) {
        this.onClickSelectLayer(category, originalLayer).then(() => {
          const entry = importedLayerRegistry.get(originalLayer.id);
          if (entry) mapService.zoomToExtent(entry.layer.getSource().getExtent());
        });
        return;
      }
      LayerService.addLayerToMap(originalLayer, null, true);
      if (!isActive) {
        this.$set(originalLayer, "selected", true);
        this.$store.dispatch("addActiveLayer", originalLayer);
        this.startLayerSession(originalLayer, this.$route.params.idMap);
      }
    },
    onUpdateOpacity: function (payload) {
      const { category, layer, opacity } = payload;
      const originalLayer = this.findLayerById(layer.id);

      if (originalLayer) {
        this.$set(originalLayer, 'opacity', opacity);

        // Actualizar opacidad en el mapa via servicio
        mapService.setLayerOpacity(layer.id, opacity);
      }
    },
    exitLayers: function () {
      
      this.selectMapTool("layers", false);
    },
    /**
     * Get layer information for attributes table
     * @param {Object} category - Category object
     * @param {Object} layer - Layer object
     */
    getLayerInfo: function (category, layer) {
      console.log("getLayerInfo", layer);
      // Find the layer in the original store by ID
      const originalLayer = this.findLayerById(layer.id);
      if (!originalLayer) {
        console.error("Layer not found in store:", layer);
        return;
      }
      const layerProvider = originalLayer.provider.name;
      switch (layerProvider) {
        case "geoserver":
          this.geoserverFeatures(originalLayer);
          break;

        case "arcgis":
          attributesTableService.openTable(originalLayer);
          break;
        case IMPORT_PROVIDER:
          attributesTableService.openTable(originalLayer);
          break;
        default:
          console.error(
            "Unsupported layer provider for attributes table:",
            layerProvider
          );
          return;
      }
    },

    /**
     * Find a layer by its ID in the original store
     * @param {string} layerId - Layer ID to find
     * @returns {Object|null} Layer object or null if not found
     */
    findLayerById: function (layerId) {
      for (const category of this.layers) {
        const layer = category.layers.find((l) => l.id === layerId);
        if (layer) {
          return layer;
        }
      }
      return null;
    },
    geoserverFeatures: function (layer) {
      console.log("geoserverFeatures", layer);
      let totalRecords = 0;
      let workspace = layer.provider.geoserverdata.workspace;
      let datastore = layer.provider.geoserverdata.datastore;
      let typeName = workspace + ":" + datastore;

      // Construct proper WFS GetFeature URL
      let baseUrl = layer.provider.url + "/wfs";
      let url =
        baseUrl +
        "?service=WFS&version=2.0.0&request=GetFeature&typeNames=" +
        encodeURIComponent(typeName) +
        "&count=1&outputFormat=application/json";
      console.log("WFS GetFeature URL: ", url);
      fetch(url)
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          totalRecords = data.totalFeatures;
          this.executeMapToolAction("attributesTable", "paginationInfo", {
            offset: 0,
            totalRecords: totalRecords,
            layer: layer,
          });
          this.selectMapTool("attributesTable", true);
          return totalRecords;
        })
        .catch((err) => {
          console.error("Error fetching WFS data: ", err);
          return 0;
        });
    },
    onClickModalNewLayer: function () {
      this.$router.push("/capas/nueva-capa");
    },

    /**
     * Alterna visibilidad sin destruir la capa: mantiene la fuente y los features ya
     * descargados en memoria, en vez de volver a pedirlos al servidor externo cada vez.
     */
    toggleWmsVisibility(layer) {
      const nextVisible = !layer.visible;
      if (!mapService.setLayerVisibility(layer.id, nextVisible)) {
        // La capa no existe en el mapa (ej. sesión recién restaurada) — crearla de cero.
        mapService.addLayer(layer);
      }
      this.$set(layer, "visible", nextVisible);
      if (nextVisible) {
        this.startExternalLayerSession(layer, this.$route.params.idMap);
      } else {
        this.endLayerSession(layer);
      }
    },

    /**
     * Ajusta la opacidad de una capa WMS de sesión (payload emitido por ListPlegable)
     */
    onWmsUpdateOpacity(payload) {
      const { layer, opacity } = payload;
      mapService.setLayerOpacity(layer.id, opacity);
    },

    /**
     * Hace zoom a una capa WMS/WFS externa usando el bbox extraído del GetCapabilities
     * al conectarla. Si el servidor no informó bbox (o no se pudo parsear), avisa en vez
     * de intentar un fit con un extent inválido.
     */
    onClickZoomWmsLayer(category, layer) {
      const bbox = layer.provider?.bbox;
      if (!bbox) {
        this.$store.dispatch("toast/show", {
          message: "Esta capa no informó una extensión (bbox) válida, no se puede hacer zoom.",
          type: "error"
        });
        return;
      }
      mapService.zoomToLonLatExtent(bbox);
    },

    /**
     * Elimina una capa WMS de sesión del mapa y del store
     */
    removeWmsLayer(layer) {
      mapService.removeLayer(layer.id);
      this.endLayerSession(layer);
      this.$store.dispatch("removeWmsSessionLayer", layer);
    },

    wmsCheckState(category) {
      const layers = category.layers || []
      if (!layers.length) return 'none'
      const visibleCount = layers.filter(l => l.visible).length
      if (visibleCount === 0) return 'none'
      return visibleCount === layers.length ? 'all' : 'some'
    },

    toggleWmsCategory(category) {
      const allVisible = (category.layers || []).every(l => l.visible)
      if (allVisible) {
        category.layers.forEach(layer => {
          mapService.setLayerVisibility(layer.id, false)
          this.$set(layer, 'visible', false)
          this.endLayerSession(layer)
        })
      } else {
        category.layers.forEach(layer => {
          if (!layer.visible) {
            if (!mapService.setLayerVisibility(layer.id, true)) {
              mapService.addLayer(layer)
            }
            this.$set(layer, 'visible', true)
            this.startExternalLayerSession(layer, this.$route.params.idMap)
          }
        })
      }
    },

  },
  updated() { },
  created() { },
  mounted() {
    this.syncImportedFiles();
    if (!this.isFixed) {
      this.makeDraggable("layersID");
    }
  },
};
</script>

<style scoped lang="scss">
.layer-tool-window{
  left: 0.5em;
	top: 4em;
}
.resizable {
  margin-top: 0.5em;
  height: 40vh;
  min-height: 20vh;
  min-width: 20vw;
  resize: both;
  overflow: auto;
}

.fixedWidget {
  margin-top: 0.5em;
  height: 100vh;
  min-height: 20vh;
  min-width: 20vw;
  resize: none;
  overflow: hidden;
}

.scrollable {
  height: 110px;
  overflow-y: scroll;
}

/* Search and Filter Section - Bold Contrast Design */
.search-filter-section {
  padding: 14px 16px;
  background: transparent;
  border-bottom: 1px solid var(--color-line);
}

.search-bar-container {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.search-bar {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  background: transparent;
  border: 1px solid var(--color-line);
  border-radius: 4px;
  padding: 8px 12px;
  transition: all 0.2s;

  // &:focus-within {
  //   border-color: #006fb3;
  //   box-shadow: 0 0 0 3px rgba(0, 111, 179, 0.1);
  // }
}

.search-icon {
  color: var(--color-graphite);
  margin-right: 8px;
  font-size: 16px;
  font-weight: bold;
}

/* Typing Dots Indicator - Conversational Design */
.typing-dots {
  display: inline-flex;
  gap: 3px;
  align-items: center;
  margin-left: 4px;

  span {
    width: 4px;
    height: 4px;
    background: var(--color-graphite);
    border-radius: 50%;
    animation: bounce-dot 1s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

@keyframes bounce-dot {

  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }

  30% {
    transform: translateY(-6px);
    opacity: 0.7;
  }
}

/* Pulse Wave Accent Bar - Minimalist Industrial */
.debounce-pulse-bar {
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg,
      transparent 0%,
      #006fb3 25%,
      #4caf50 50%,
      #006fb3 75%,
      transparent 100%);
  background-size: 200% 100%;
  animation: pulse-wave 1.2s ease-in-out infinite;
  border-radius: 1px;
}

@keyframes pulse-wave {

  0%,
  100% {
    background-position: 200% 0;
    opacity: 0.7;
  }

  50% {
    background-position: -200% 0;
    opacity: 1;
  }
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: #222;
  font-weight: 500;

  // &::placeholder {
  //   color: #999;
  //   font-weight: normal;
  // }
}

.clear-button {
  background: transparent;
  border: none;
  color: var(--color-graphite);
  cursor: pointer;
  padding: 4px 8px;
  transition: all 0.2s;
  font-size: 14px;

  // &:hover {
  //   color: #8b332f;
  //   transform: scale(1.1);
  // }
}

.search-button {
  min-width: 80px;
  font-weight: bold;
  border: 1px solid var(--color-line);
  border-radius: 4px;
  padding: 8px 12px;
  background: var(--color-navy);
  color: var(--color-white);
  &:disabled {
    background: transparent;
    cursor: not-allowed;
    color: var(--color-graphite);
  }
}

/* Filter Type Selector: mismo look que las pestañas Población/Infraestructura
   del modal de resultado de geoproceso, más compacto. */
.filter-type-selector {
  display: flex;
  width: fit-content;
  margin: 10px auto 0;
  padding: 3px;
  gap: 3px;
  background: var(--color-mist, #f2f4f6);
  border: 1px solid var(--color-line);
  border-radius: 6px;
}

.filter-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  color: var(--color-ghost, #8a929a);

  &:hover {
    background: var(--color-white);
    color: var(--color-navy);
  }

  &.active {
    background: var(--color-navy);
    color: var(--color-white);
    border-color: var(--color-navy);
    box-shadow: 0 2px 5px rgba(14, 42, 64, 0.16);
  }
}

.btn-clean-filter {
	color: var(--color-graphite);
	background: transparent;
	border: solid 1px var(--color-line);
  &:hover{
    background: var(--color-navy);
    color: var(--color-white);
  }
}

/* Result Summary */
.result-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding: 10px 12px;
  background: transparent;
  border: 1px solid var(--color-line);
  border-radius: 4px;
  font-size: 13px;
}

.result-count {
  font-weight: 700;
  color: var(--color-navy);
  font-size: 14px;
}

.reset-button {

  border: 1px solid var(--color-line);
  color: var(--color-graphite);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 4px;
  transition: all 0.15s;
  background: var(--color-navy);
  color: var(--color-white);

  &:hover {

    color: var(--color-graphite);
    background: transparent;
  }
}

.collapse-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 6px;
}

.link-button {
  background: transparent;
  border: none;
  color: var(--color-steel);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  padding: 4px 8px;

  &:hover {
    color: var(--color-navy);
    background: rgba(0, 111, 179, 0.05);
    border-radius: 4px;
  }
}

/* Loading State */
.loading-state {
  padding: 16px;
}

.skeleton-item {
  height: 50px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 10px;
  border-radius: 6px;
  border: 2px solid #ddd;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.empty-icon {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}

.empty-state p {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Capas externas WMS (sesión) — mismas categorías/filas que ListPlegable, solo el encabezado de sección es propio */
.wms-ext-heading {
  font-weight: 700;
  font-size: 13px;
  color: var(--color-navy);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 12px 4px 4px;
}
</style>
